// Third party
import React, { Component } from 'react';
import Drawer from 'react-modern-drawer';
import FocusTrap from 'focus-trap-react';
import { connect } from 'react-redux';
import { Waypoint } from 'react-waypoint';
import { debounce } from 'lodash';

// Utils
import { FormattedMessage } from '../../../utils/i18n';

// Components
import { CloseButton } from '../buttons';
import { NotificationItem } from './NotificationItem';
import { LoadingSpinner } from '../common';
import MaterialChangeItem from './MaterialChangeItem';
import { NotificationDetailsModal } from '../modals/notification-center';

// Redux actions
import { ReduxState } from '../../../redux/modules/types';
import { UserAnnouncement, AnnouncementType } from '../../../redux/modules/types/announcements';
import {
  getAnnouncements,
  openNotificationCenter,
  closeNotificationCenter,
  markAnnouncementsAsRead,
  getAnnouncementCounts,
  readAndAcknowledgeAnnouncement,
} from '../../../redux/modules/announcements/user';

// Styles
import 'react-modern-drawer/dist/index.css';

type DispatchProps = {
  getAnnouncements: (query?: string, next?: string) => Promise<any>;
  readAndAcknowledgeAnnouncement: (id: string) => Promise<any>;
  openNotificationCenter: () => void;
  closeNotificationCenter: () => void;
  markAnnouncementsAsRead: (ids: string[]) => void;
  getAnnouncementCounts: () => void;
};

type StateProps = {
  announcements: UserAnnouncement[];
  next?: string;
  isOpen: boolean;
  unreadCount: number;
};

type State = {
  isLoading: boolean;
  isLoadingMore: boolean;
  queueRead: string[];
  selectedNotificationId?: string;
  showNotificationDetailsModal: boolean;
};

type Props = DispatchProps & StateProps;

const MAX_DRAWER_SIZE = 680;

export class NotificationCenter extends Component<Props, State> {
  readonly state: State = {
    isLoading: false,
    isLoadingMore: false,
    queueRead: [],
    showNotificationDetailsModal: false,
  };

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
    const { isOpen } = this.props;
    if (prevProps.isOpen !== isOpen && isOpen) {
      this.setState({ isLoading: true });
      this.props.getAnnouncements().then(() => {
        this.setState({ isLoading: false });
      });
    }
  }

  closeDrawer = () => {
    if (this.state.showNotificationDetailsModal) return;
    this.props.getAnnouncementCounts();
    this.props.closeNotificationCenter();
  };

  queueForRead = (id: string) => {
    const { queueRead } = this.state;
    if (!queueRead.includes(id)) {
      this.setState((state: State) => ({ queueRead: [...state.queueRead, id] }));
    }
    this.markAsRead();
  };

  markAsRead = debounce(() => {
    const { queueRead } = this.state;
    this.props.markAnnouncementsAsRead(queueRead);
  }, 500);

  loadMoreNotifications = () => {
    const { next } = this.props;
    this.setState({ isLoadingMore: true });
    this.props.getAnnouncements(undefined, next).then(() => {
      this.setState({ isLoadingMore: false });
    });
  };

  renderNotificationCards = () => {
    const { announcements } = this.props;

    return announcements.map(announcement => {
      if (announcement?.notificationType === AnnouncementType.MATERIAL_CHANGE) {
        return <MaterialChangeItem key={announcement._id} announcement={announcement} />;
      }

      return (
        <NotificationItem
          key={announcement._id}
          announcement={announcement}
          onReadMore={() => {
            this.setState({
              selectedNotificationId: announcement._id,
              showNotificationDetailsModal: true,
            });
          }}
          onVisible={this.queueForRead}
        />
      );
    });
  };

  renderEmptyNotifications = () => (
    <div className="no-notifications">
      <div>
        <i className="icon-notification-bell" />
        <span>
          <FormattedMessage id="AnnouncementsAdvisor.NoNotificationsYet" />
        </span>
      </div>
    </div>
  );

  focusTrapOptions = {
    preventScroll: false,
    clickOutsideDeactivates: true,
    escapeDeactivates: true,
    onDeactivate: this.closeDrawer,
    checkCanFocusTrap: (trapContainers: Array<HTMLElement | SVGElement>): Promise<any> => {
      const results = trapContainers.map(
        trapContainer =>
          new Promise<void>(resolve => {
            const interval = setInterval(() => {
              if (getComputedStyle(trapContainer).visibility !== 'hidden') {
                resolve();
                clearInterval(interval);
              }
            }, 500);
          }),
      );

      return Promise.all(results);
    },
  };

  render() {
    const { announcements, next, isOpen, unreadCount } = this.props;
    const { isLoading, isLoadingMore, selectedNotificationId, showNotificationDetailsModal } = this.state;

    return (
      <>
        <Drawer open={isOpen} onClose={this.closeDrawer} direction="right" size={window.innerWidth > MAX_DRAWER_SIZE ? MAX_DRAWER_SIZE : '100%'} zIndex={1002}>
          <FocusTrap active={isOpen} focusTrapOptions={this.focusTrapOptions}>
            <div className="notification-center">
              <header className="notification-center-header">
                <CloseButton onClick={this.closeDrawer} />
                <h3>
                  <FormattedMessage id="AnnouncementsAdvisor.NotificationCenter" />
                </h3>
                <div>
                  <span className="font-weight-bold">{unreadCount}</span> <FormattedMessage id="AnnouncementsAdvisor.Unread" />
                </div>
              </header>
              <section className="notifications">
                {isLoading && <LoadingSpinner />}
                {!isLoading && !announcements.length && this.renderEmptyNotifications()}
                {!isLoading && announcements.length > 0 && this.renderNotificationCards()}
                {!isLoading && isLoadingMore && (
                  <div className="loading-more">
                    <LoadingSpinner />
                  </div>
                )}
                {!isLoading && !isLoadingMore && next != null && <Waypoint onEnter={this.loadMoreNotifications} />}
              </section>
            </div>
          </FocusTrap>
        </Drawer>

        <NotificationDetailsModal
          announcementId={selectedNotificationId}
          show={showNotificationDetailsModal}
          onCancel={() => this.setState({ showNotificationDetailsModal: false })}
          onClosed={() => this.setState({ selectedNotificationId: undefined })}
          onAccept={() => {
            if (!selectedNotificationId) return;
            this.props.readAndAcknowledgeAnnouncement(selectedNotificationId).then(() => {
              this.setState({ showNotificationDetailsModal: false });
            });
          }}
        />
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState): StateProps => ({
  announcements: state.userAnnouncements.announcements,
  next: state.userAnnouncements.nextToken,
  isOpen: state.userAnnouncements.showNotificationCenter,
  unreadCount: state.userAnnouncements.unreadCount,
});

export default connect(mapStateToProps, {
  getAnnouncements,
  openNotificationCenter,
  closeNotificationCenter,
  markAnnouncementsAsRead,
  getAnnouncementCounts,
  readAndAcknowledgeAnnouncement,
})(NotificationCenter);
